import type { Drill, Hint } from "./types.ts";
import { HARNESS_KIT } from "./harness-kit.ts";

// The harness is plain string concatenation rather than a template literal so
// that solution code containing backticks or ${...} never needs escaping.
const harness = [
  HARNESS_KIT,
  'if (typeof getExecutionOrder !== "function") {',
  '  throw new Error("getExecutionOrder is not defined. Keep the function name as given.");',
  "}",
  "",
  "const __PIPELINE = [",
  '  { id: "clean", dependencies: [] },',
  '  { id: "lint", dependencies: [] },',
  '  { id: "compile", dependencies: ["clean"] },',
  '  { id: "test", dependencies: ["compile", "lint"] },',
  '  { id: "bundle", dependencies: ["compile"] },',
  '  { id: "build", dependencies: ["test", "bundle"] },',
  '  { id: "deploy", dependencies: ["build"] }',
  "];",
  "",
  "// A graph where a and b depend on each other, plus an unrelated task that",
  "// depends on nothing. Used both to check a cycle is detected AND to check that",
  "// a cycle somewhere in the list does not poison a target that never touches it.",
  "const __CYCLIC = [",
  '  { id: "a", dependencies: ["b"] },',
  '  { id: "b", dependencies: ["a"] },',
  '  { id: "loner", dependencies: [] }',
  "];",
  "",
  'const __SELF = [{ id: "a", dependencies: ["a"] }];',
  "",
  "// The set of tasks genuinely required to run the targets. Uses its own visited",
  "// set so a cyclic fixture cannot hang the checker itself.",
  "function __closure(tasks, targets) {",
  "  const byId = new Map(tasks.map(function (t) { return [t.id, t]; }));",
  "  const need = new Set();",
  "  const stack = targets.slice();",
  "  while (stack.length) {",
  "    const id = stack.pop();",
  "    if (need.has(id)) continue;",
  "    need.add(id);",
  "    const task = byId.get(id);",
  "    if (task) for (const d of task.dependencies) stack.push(d);",
  "  }",
  "  return need;",
  "}",
  "",
  "// Order among siblings is genuinely free, so this checks the four properties the",
  "// spec actually states rather than one blessed array.",
  "function __expectValidOrder(call, fn, tasks, targets) {",
  "  const r = __attempt(fn);",
  "  if (!r.ok) {",
  '    __fail(call, ["expected a valid execution order", "but it threw: " + r.error]);',
  "  }",
  "  const result = r.value;",
  "  if (!Array.isArray(result)) {",
  '    __fail(call, ["expected an array", "but got " + __show(result)]);',
  "  }",
  "  const seen = new Set();",
  "  for (const id of result) {",
  "    if (seen.has(id)) {",
  '      __fail(call, ["\\"" + id + "\\" appears more than once", "got " + __show(result)]);',
  "    }",
  "    seen.add(id);",
  "  }",
  "  const need = __closure(tasks, targets);",
  "  for (const id of need) {",
  "    if (!seen.has(id)) {",
  '      __fail(call, ["\\"" + id + "\\" is required but missing", "got " + __show(result)]);',
  "    }",
  "  }",
  "  for (const id of seen) {",
  "    if (!need.has(id)) {",
  '      __fail(call, ["\\"" + id + "\\" is not needed to run " + __show(targets),',
  '                   "got " + __show(result)]);',
  "    }",
  "  }",
  "  const byId = new Map(tasks.map(function (t) { return [t.id, t]; }));",
  "  const at = new Map(result.map(function (id, i) { return [id, i]; }));",
  "  for (const id of result) {",
  "    const task = byId.get(id);",
  "    if (!task) continue;",
  "    for (const dep of task.dependencies) {",
  "      if (at.get(dep) > at.get(id)) {",
  '        __fail(call, ["\\"" + dep + "\\" must run before \\"" + id + "\\"", "got " + __show(result)]);',
  "      }",
  "    }",
  "  }",
  "}",
  "",
  "const __tests = [",
  "  {",
  '    name: "single target with no dependencies",',
  '    about: "The simplest case: a task that needs nothing is just itself.",',
  "    run: function () {",
  '      __expectExact("getExecutionOrder(pipeline, [\\"clean\\"])",',
  '        function () { return getExecutionOrder(__PIPELINE, ["clean"]); }, ["clean"]);',
  "    }",
  "  },",
  "  {",
  '    name: "target with a linear dependency",',
  '    about: "One level of nesting. The dependency has to come out first.",',
  "    run: function () {",
  '      __expectExact("getExecutionOrder(pipeline, [\\"compile\\"])",',
  '        function () { return getExecutionOrder(__PIPELINE, ["compile"]); }, ["clean", "compile"]);',
  "    }",
  "  },",
  "  {",
  '    name: "shared dependencies run once, in a valid order",',
  '    about: "Both test and bundle need compile. It must appear once, before either of them.",',
  "    run: function () {",
  '      __expectValidOrder("getExecutionOrder(pipeline, [\\"build\\"])",',
  '        function () { return getExecutionOrder(__PIPELINE, ["build"]); }, __PIPELINE, ["build"]);',
  "    }",
  "  },",
  "  {",
  '    name: "multiple targets",',
  '    about: "Two targets resolved together, sharing whatever they have in common.",',
  "    run: function () {",
  '      __expectValidOrder("getExecutionOrder(pipeline, [\\"lint\\", \\"bundle\\"])",',
  '        function () { return getExecutionOrder(__PIPELINE, ["lint", "bundle"]); }, __PIPELINE, ["lint", "bundle"]);',
  "    }",
  "  },",
  "  {",
  '    name: "only what is strictly necessary",',
  "    hidden: true,",
  '    about: "Catches sorting the whole graph instead of just what the target needs.",',
  "    run: function () {",
  '      __expectExact("getExecutionOrder(pipeline, [\\"lint\\"])",',
  '        function () { return getExecutionOrder(__PIPELINE, ["lint"]); }, ["lint"]);',
  "    }",
  "  },",
  "  {",
  '    name: "the deepest target pulls the whole graph, still once each",',
  "    hidden: true,",
  '    about: "deploy sits at the top, so everything is required, and still exactly once.",',
  "    run: function () {",
  '      __expectValidOrder("getExecutionOrder(pipeline, [\\"deploy\\"])",',
  '        function () { return getExecutionOrder(__PIPELINE, ["deploy"]); }, __PIPELINE, ["deploy"]);',
  "    }",
  "  },",
  "  {",
  '    name: "a repeated target is not emitted twice",',
  "    hidden: true,",
  '    about: "Deduplication applies to the targets you were handed, not just to shared dependencies.",',
  "    run: function () {",
  '      __expectExact("getExecutionOrder(pipeline, [\\"clean\\", \\"clean\\"])",',
  '        function () { return getExecutionOrder(__PIPELINE, ["clean", "clean"]); }, ["clean"]);',
  "    }",
  "  },",
  "  {",
  '    name: "overlapping targets share their dependencies",',
  "    hidden: true,",
  '    about: "test and bundle overlap on compile and clean; neither may be emitted twice.",',
  "    run: function () {",
  '      __expectValidOrder("getExecutionOrder(pipeline, [\\"test\\", \\"bundle\\"])",',
  '        function () { return getExecutionOrder(__PIPELINE, ["test", "bundle"]); }, __PIPELINE, ["test", "bundle"]);',
  "    }",
  "  },",
  "  {",
  '    name: "no targets means nothing to run",',
  "    hidden: true,",
  '    about: "An empty request is not an error, and not the whole graph either.",',
  "    run: function () {",
  '      __expectExact("getExecutionOrder(pipeline, [])",',
  "        function () { return getExecutionOrder(__PIPELINE, []); }, []);",
  "    }",
  "  },",
  "  {",
  '    name: "a circular dependency throws",',
  "    hidden: true,",
  '    about: "cyclic = [a needs b, b needs a, loner needs nothing]. Asking for a can never be satisfied.",',
  "    run: function () {",
  '      __expectThrows("getExecutionOrder(cyclic, [\\"a\\"])  // cyclic = [a needs b, b needs a, loner needs nothing]",',
  '        function () { return getExecutionOrder(__CYCLIC, ["a"]); },',
  '        "a needs b and b needs a, so there is no order that satisfies both");',
  "    }",
  "  },",
  "  {",
  '    name: "a task depending on itself throws",',
  "    hidden: true,",
  '    about: "A self-loop is the shortest possible cycle, and is easy to miss if you only compare pairs.",',
  "    run: function () {",
  '      __expectThrows("getExecutionOrder(selfDep, [\\"a\\"])  // selfDep = [a needs a]",',
  '        function () { return getExecutionOrder(__SELF, ["a"]); },',
  '        "a depends on itself, which can never be satisfied");',
  "    }",
  "  },",
  "  {",
  '    name: "a cycle elsewhere in the graph does not break an unrelated target",',
  "    hidden: true,",
  '    about: "loner needs nothing and touches neither a nor b, so it must resolve normally even though the same list contains a cycle. Only walk what the target actually reaches, and only reject a cycle you actually walk into. A common cause of failing this while passing the others is reading a hardcoded graph instead of the tasks argument.",',
  "    run: function () {",
  '      __expectExact("getExecutionOrder(cyclic, [\\"loner\\"])  // cyclic = [a needs b, b needs a, loner needs nothing]",',
  '        function () { return getExecutionOrder(__CYCLIC, ["loner"]); }, ["loner"]);',
  "    }",
  "  },",
  "  {",
  '    name: "a generated graph, different every time you see this drill",',
  '    about: "Tasks and dependencies built from the attempt seed, checked against the four rules rather than one accepted order. The inputs change the next time this drill comes round, so remembering an answer is not a way through.",',
  "    run: function () {",
  '      const g = __gen("random-dag");',
  "      const size = g.int(8, 14);",
  "      const tasks = [];",
  "      for (let i = 0; i < size; i++) {",
  "        const deps = [];",
  '        for (let j = 0; j < i; j++) if (g.next() < 0.25) deps.push("t" + j);',
  '        tasks.push({ id: "t" + i, dependencies: deps });',
  "      }",
  "      // Shuffled: declaration order is already a valid answer here, and code",
  "      // that returns the list it was handed must not pass by luck.",
  "      const graph = g.shuffle(tasks);",
  "      const ids = g.shuffle(tasks.map(function (t) { return t.id; }));",
  "      const targets = ids.slice(0, g.int(1, 3));",
  '      __expectValidOrder("getExecutionOrder(generated, " + __show(targets) + ")",',
  "        function () { return getExecutionOrder(graph, targets); }, graph, targets);",
  "    }",
  "  },",
  "  {",
  '    name: "a generated graph with a cycle bolted on",',
  "    hidden: true,",
  '    about: "The same kind of generated graph plus two tasks that need each other. Asking for one of those must throw, and asking for something that never reaches them must still resolve. Walking the whole list rather than only what the target needs fails the second half.",',
  "    run: function () {",
  '      const g = __gen("random-cycle");',
  "      const size = g.int(6, 10);",
  "      const tasks = [];",
  "      for (let i = 0; i < size; i++) {",
  "        const deps = [];",
  '        for (let j = 0; j < i; j++) if (g.next() < 0.3) deps.push("t" + j);',
  '        tasks.push({ id: "t" + i, dependencies: deps });',
  "      }",
  '      const graph = g.shuffle(tasks.concat([',
  '        { id: "spin", dependencies: ["wheel"] },',
  '        { id: "wheel", dependencies: ["spin"] }',
  "      ]));",
  '      __expectThrows("getExecutionOrder(generated, [\\"spin\\"])  // spin needs wheel, wheel needs spin",',
  '        function () { return getExecutionOrder(graph, ["spin"]); },',
  '        "spin and wheel need each other, so no order satisfies both");',
  "      const target = tasks[g.int(0, tasks.length - 1)].id;",
  '      __expectValidOrder("getExecutionOrder(generated, [" + __show(target) + "])  // the cycle is elsewhere in the list",',
  "        function () { return getExecutionOrder(graph, [target]); }, graph, [target]);",
  "    }",
  "  }",
  "];",
].join("\n");

const starter = `type Task = {
  id: string;
  dependencies: string[];
};

// A sample graph, for reading. The tests build their own.
const pipelineData: Task[] = [
  { id: "clean", dependencies: [] },
  { id: "lint", dependencies: [] },
  { id: "compile", dependencies: ["clean"] },
  { id: "test", dependencies: ["compile", "lint"] },
  { id: "bundle", dependencies: ["compile"] },
  { id: "build", dependencies: ["test", "bundle"] },
  { id: "deploy", dependencies: ["build"] }
];

/**
 * Resolves the execution order for a given set of target tasks.
 *
 * @param tasks - The complete list of available tasks and their dependencies.
 * @param targetTasks - An array of task IDs we want to execute.
 * @returns An array of task IDs in the correct execution order.
 */
function getExecutionOrder(tasks: Task[], targetTasks: string[]): string[] {
  // TODO
  return [];
}
`;

const solution = `type Task = { id: string; dependencies: string[] };

function getExecutionOrder(tasks: Task[], targetTasks: string[]): string[] {
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const order: string[] = [];
  // Three states, not two. "visiting" is what makes this cycle detection rather
  // than plain deduplication: meeting a node that is still on the stack means we
  // have walked back into the path we came in on.
  const state = new Map<string, "visiting" | "done">();

  function visit(id: string): void {
    const seen = state.get(id);
    if (seen === "done") return;
    if (seen === "visiting") {
      throw new Error(\`Circular dependency detected at "\${id}"\`);
    }
    state.set(id, "visiting");
    const task = byId.get(id);
    for (const dep of task?.dependencies ?? []) {
      visit(dep);
    }
    state.set(id, "done");
    // Push on the way out, so every dependency is already in the array.
    order.push(id);
  }

  for (const target of targetTasks) {
    visit(target);
  }
  return order;
}`;

const hints: Hint[] = [
  {
    label: "Nudge",
    body: "You are producing an order in which every task appears after the things it needs. What do you get for free if you visit a task's dependencies before you emit the task itself? Push on the way out of the recursion, not on the way in.",
    code: `function getExecutionOrder(tasks: Task[], targetTasks: string[]): string[] {
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const order: string[] = [];

  function visit(id: string): void {
    for (const dep of byId.get(id)?.dependencies ?? []) visit(dep);
    order.push(id);   // out, not in: every dependency is already in the array
  }

  for (const target of targetTasks) visit(target);
  return order;   // already ordered, so there is no sort at the end
}`,
  },
  {
    label: "Approach",
    body: "That walk is correct on a tree and wrong on anything real: a shared dependency is emitted twice and a cycle runs forever. Both are fixed by marking nodes, and it takes two marks rather than one.",
    code: `// Three states, not two. "visiting" is what makes this cycle detection rather
// than plain deduplication: meeting a node still on the stack means we have
// walked back into the path we came in on.
const state = new Map<string, "visiting" | "done">();

function visit(id: string): void {
  const seen = state.get(id);
  if (seen === "done") return;                       // shared dependency, already emitted
  if (seen === "visiting") throw new Error(\`Circular dependency detected at "\${id}"\`);

  state.set(id, "visiting");
  for (const dep of byId.get(id)?.dependencies ?? []) visit(dep);
  state.set(id, "done");
  order.push(id);
}`,
  },
  {
    label: "The trap",
    body: "With a single mark you get one of two wrong answers and not the other: either you loop forever on a genuine cycle, or you report a cycle on a diamond that has none. Both marks earn their place. Note also that only the reachable subgraph is walked, so asking for one leaf task must not drag in the whole file.",
  },
];

export const taskDependencyResolver: Drill = {
  id: "task-dependency-resolver",
  track: "typescript",
  title: "The Task Dependency Resolver",
  pattern: "topological-sort",
  difficulty: "medium",
  basePoints: 250,
  timeBudgetMs: 25 * 60 * 1000,
  statement: `You are building the core engine for a lightweight task runner, similar to \`npm run\` or \`make\`. Someone asks for \`build\`, and it is your job to work out everything that has to happen first, and in what order.

Each task declares the tasks it depends on. Write \`getExecutionOrder(tasks, targetTasks)\`, which takes the full list of tasks plus the ones the user asked for, and returns the task IDs in an order that can be executed top to bottom.

## The shape of the data

\`\`\`ts
type Task = {
  id: string;
  dependencies: string[];   // ids that must run BEFORE this one
};
\`\`\`

The sample graph in the editor reads like this:

\`\`\`
clean     needs nothing
lint      needs nothing
compile   needs clean
test      needs compile, lint
bundle    needs compile
build     needs test, bundle
deploy    needs build
\`\`\`

Note that both \`test\` and \`bundle\` need \`compile\`. That shared dependency is the interesting part: \`compile\` has to run before either of them, and it has to run exactly once even though two tasks asked for it.

## Requirements

- **Correct ordering.** Every dependency appears before the task that depends on it.
- **Deduplication.** No task ID appears twice, no matter how many tasks needed it.
- **Efficiency.** Only tasks actually required by the targets. Asking for \`lint\` must not drag in the whole file.
- **Error handling.** Throw if there is a cycle, for example A depends on B and B depends on A. Returning a partial answer is not acceptable: an unrunnable graph is an error, not a shorter list.

## Examples

A target with nothing underneath it is just itself:

\`\`\`ts
getExecutionOrder(pipelineData, ["clean"])
// → ["clean"]
\`\`\`

A chain is walked to the bottom, deepest first:

\`\`\`ts
getExecutionOrder(pipelineData, ["compile"])
// → ["clean", "compile"]
\`\`\`

A shared dependency is emitted once, before both of the tasks that need it:

\`\`\`ts
getExecutionOrder(pipelineData, ["build"])
// → ["clean", "compile", "lint", "test", "bundle", "build"]
// "clean" before "compile"; "compile" before both "test" and "bundle";
// both of those before "build". "deploy" is absent: nothing asked for it.
\`\`\`

Multiple targets are resolved together, sharing whatever they have in common:

\`\`\`ts
getExecutionOrder(pipelineData, ["lint", "bundle"])
// → ["lint", "clean", "compile", "bundle"]
\`\`\`

A cycle throws rather than returning:

\`\`\`ts
const cyclic = [
  { id: "a", dependencies: ["b"] },
  { id: "b", dependencies: ["a"] },
];
getExecutionOrder(cyclic, ["a"])   // throws
\`\`\`

## On the expected output

**Order among siblings is not fixed.** In the \`["build"]\` example, \`lint\` could equally appear first, or between \`clean\` and \`compile\`; nothing in the problem says which of two independent tasks goes first. The tests check the properties the requirements actually state (dependencies precede dependents, no duplicates, nothing unnecessary), not that you reproduced one particular array. Any correct order passes.

Two of the cases also build their own graph rather than using the one above, and it is a different graph each time this drill comes round. They check the same four rules. Code that returns the tasks in the order it was handed them passes the fixed cases and fails these.`,
  hints,
  starter,
  harness,
  solution,
  complexity:
    "O(V + E) over the reachable subgraph. Each task is visited once and each dependency edge is followed once.",
};
